import { Injectable, signal, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { PermissionDto, PermissionGroupedDto, UserDto, UserDtoPOST } from '@dto';
import { RequestMethodEnum, RequestOptions } from '@model';
import { GenericService } from './generic.service';
import { GenericOldService } from './generic-old.service';

@Injectable()
export class UserService {
  private _users: WritableSignal<UserDto[] | undefined> = signal(undefined);
  get users(): WritableSignal<UserDto[] | undefined> {
    return this._users;
  }

  private _groupedPermissions: WritableSignal<PermissionGroupedDto | undefined> = signal(undefined);
  get groupedPermissions(): WritableSignal<PermissionGroupedDto | undefined> {
    return this._groupedPermissions;
  }

  private _user: WritableSignal<UserDto | undefined> = signal(undefined);
  get user(): WritableSignal<UserDto | undefined> {
    return this._user;
  }

  private _isAdmin: boolean;

  constructor(
    private resourceService: GenericOldService<any>,
    private genericService: GenericService,
  ) {}

  get isAdmin() {
    return this._isAdmin;
  }

  users$(): Observable<UserDto[]> {
    return this.resourceService.get('users').pipe(
      tap((users: UserDto[]) => {
        this._users.set(users);
      }),
    );
  }

  createNewUser(user: UserDtoPOST): Observable<UserDto> {
    return this.resourceService
      .post('users', user)
      .pipe(switchMap((userCreated: UserDto) => this.users$().pipe(map(() => userCreated))));
  }

  getUserDetails(): Observable<UserDto> {
    const options: RequestOptions<undefined, UserDto> = {
      method: RequestMethodEnum.GET,
      path: `api/v1/users/details`,
    };
    return this.genericService.request(options).pipe(
      tap((user: UserDto) => {
        this._user.set(user);
      }),
    );
  }

  userGroupedPermissions$(userId: string): Observable<PermissionGroupedDto> {
    const options: RequestOptions<undefined, PermissionGroupedDto> = {
      method: RequestMethodEnum.GET,
      path: `api/v1/users/${userId}/grouped-permissions`,
    };
    return this.genericService.request(options).pipe(
      tap((groupedPermissions: PermissionGroupedDto) => {
        this._groupedPermissions.set(groupedPermissions);
      }),
    );
  }

  addUserPermission$(userId: string, permission: PermissionDto): Observable<void> {
    const options: RequestOptions<PermissionDto, void> = {
      body: permission,
      method: RequestMethodEnum.POST,
      path: `api/v1/users/${userId}/permissions`,
    };
    return this.genericService.request(options);
  }

  deleteUserPermission$(userId: string, permission: PermissionDto): Observable<void> {
    const options: RequestOptions<PermissionDto, void> = {
      body: permission,
      method: RequestMethodEnum.DELETE,
      path: `api/v1/users/${userId}/permissions`,
    };
    return this.genericService.request(options);
  }
}
